// src/components/Admin/ContactMessages.js
import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { Mail, Phone, CheckCircle2, RotateCcw, Inbox } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "resolved", label: "Resolved" },
];

function formatDate(ts) {
  if (!ts) return "";
  // Firestore Timestamp -> Date
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

export default function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, "contactMessages"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Load contactMessages error:", err);
      setError("Could not load messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await updateDoc(doc(db, "contactMessages", id), { status });
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, status } : m))
      );
    } catch (err) {
      console.error("Update status error:", err);
    } finally {
      setBusyId(null);
    }
  };

  const shown = messages.filter((m) =>
    filter === "all" ? true : (m.status || "new") === filter
  );

  const newCount = messages.filter((m) => (m.status || "new") === "new").length;

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Contact Messages</h2>
          <p className="text-gray-500 text-sm">
            {newCount} new · {messages.length} total
          </p>
        </div>

        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === f.id
                  ? "bg-[#006C35] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-gray-500 py-8">Loading messages...</div>}
      {error && <div className="text-red-600 py-8">{error}</div>}

      {!loading && !error && shown.length === 0 && (
        <div className="flex flex-col items-center justify-center text-gray-400 py-16">
          <Inbox size={40} className="mb-3" />
          <p>No messages here.</p>
        </div>
      )}

      <div className="space-y-4">
        {shown.map((m) => {
          const status = m.status || "new";
          const isNew = status === "new";
          return (
            <div
              key={m.id}
              className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">
                      {m.name || "Unknown"}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        isNew
                          ? "bg-[#006C35]/10 text-[#006C35]"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {isNew ? "New" : "Resolved"}
                    </span>
                    {m.lang && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase">
                        {m.lang}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                    {m.email && (
                      <a
                        href={`mailto:${m.email}`}
                        className="inline-flex items-center gap-1 hover:text-[#006C35]"
                      >
                        <Mail size={14} /> {m.email}
                      </a>
                    )}
                    {m.phone && (
                      <a
                        href={`tel:${m.phone}`}
                        className="inline-flex items-center gap-1 hover:text-[#006C35]"
                      >
                        <Phone size={14} /> {m.phone}
                      </a>
                    )}
                  </div>
                </div>

                <div className="text-xs text-gray-400 shrink-0">
                  {formatDate(m.createdAt)}
                </div>
              </div>

              {m.subject && (
                <div className="mt-3 font-medium text-gray-700">
                  {m.subject}
                </div>
              )}
              <p className="mt-1 text-gray-600 whitespace-pre-wrap leading-relaxed">
                {m.message}
              </p>

              <div className="mt-4 flex justify-end">
                {isNew ? (
                  <button
                    onClick={() => setStatus(m.id, "resolved")}
                    disabled={busyId === m.id}
                    className="inline-flex items-center gap-2 bg-[#006C35] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#005428] transition-colors disabled:opacity-60"
                  >
                    <CheckCircle2 size={16} />
                    Mark resolved
                  </button>
                ) : (
                  <button
                    onClick={() => setStatus(m.id, "new")}
                    disabled={busyId === m.id}
                    className="inline-flex items-center gap-2 border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
                  >
                    <RotateCcw size={16} />
                    Reopen
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}